import React, { memo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReactECharts from 'echarts-for-react';

import { formatNumberToB_N1, formatNumber_N1 } from '../../helpers/utils';

const upColor = '#54B770', downColor = '#E46161', textColor = '#D6D8DA';

export default memo(function IDXCandleStickIndayChart(props) {
    const { indexCode } = props;
    const { t } = useTranslation();
    const refChart = useRef(null);
    const refZoomSetting = useRef(null);

    //
    const c_counter = useSelector(state => state.gIndex.statisticChart[indexCode]?.counter);
    const c_chartDatas = useSelector(state => state.gIndex.statisticChart[indexCode]?.datas);

    //
    const _getChartOptions = () => {
        let _dataSet = [];

        const markLinePoints = [];

        if (c_chartDatas && c_chartDatas.length > 0) {
            let prevClose = 0;
            c_chartDatas.sort((a, b) => a.time - b.time).forEach(item => {
                const date = new Date(item.time);
                const time = date.getHours().toString().padStart(2, "0") + ":" + date.getMinutes().toString().padStart(2, "0");
                const timeInNumber = Number(`${date.getHours().toString().padStart(2, "0")}${date.getMinutes().toString().padStart(2, "0")}`);

                if (item.open == item.close
                    && item.open == item.lowest
                    && item.open == item.highest
                    && timeInNumber >= 1130
                    && timeInNumber <= 1300
                ) {

                    markLinePoints.push({ time: time, value: item.open });
                }

                let _sign = 1;
                if (item.open > item.close) {
                    _sign = -1;
                }
                else if (item.open < item.close) {
                    _sign = 1;
                }
                else if (prevClose < item.close) {
                    _sign = 1;
                }
                else if (prevClose > item.close) {
                    _sign = -1;
                }

                // [open, close, lowest, highest]
                const pointData = [
                    time,
                    item.open,
                    item.close,
                    item.lowest,
                    item.highest,
                    item.volume,
                    _sign
                ];
                //
                prevClose = item.close;
                _dataSet.push(pointData);
            });
        }

        //
        return {
            animation: false,
            useUTC: false,
            textStyle: {
                fontFamily: "tahoma, 'Be Vietnam Pro'",
            },
            dataset: {
                source: _dataSet
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross'
                },
                borderWidth: 1,
                padding: 10,
                confine: true,
                formatter: function (params) {
                    // [time, open, close, lowest, highest, volume, sign]
                    // console.log(params);
                    if (params && params.length > 0) {
                        const data = params[0].data;
                        const marker = params[0].marker;
                        const subMarker = '<span style="display:inline-block;margin-right:4px;margin-bottom:3px;border-radius:5px;width:5px;height:5px;width:5px;background-color:' + params[0].color + ';"></span>';
                        //
                        let rez = '<p>' + data[0] + '</p>';
                        rez += '<div class="chart-tooltip-grid-3">';
                        rez += '<div style="grid-column: 1 / span 3; padding: 3px 0"></div>';
                        rez += '<div>' + marker + '</div><div>' + t('giaTriChiSo') + '</div><div class="text-right"></div>';
                        rez += '<div class="text-center">' + subMarker + '</div><div>' + t('mo') + ':</div><div class="text-right"><b>' + formatNumber_N1(data[1]) + '</b></div>';
                        rez += '<div class="text-center">' + subMarker + '</div><div>' + t('dong') + ':</div><div class="text-right"><b>' + formatNumber_N1(data[2]) + '</b></div>';
                        rez += '<div class="text-center">' + subMarker + '</div><div>' + t('thap') + ':</div><div class="text-right"><b>' + formatNumber_N1(data[3]) + '</b></div>';
                        rez += '<div class="text-center">' + subMarker + '</div><div>' + t('cao') + ':</div><div class="text-right"><b>' + formatNumber_N1(data[4]) + '</b></div>';
                        rez += '<div style="grid-column: 1 / span 3; padding: 3px 0"></div>';
                        rez += '<div>' + marker + '</div><div>' + t('tongKL') + ':</div><div class="text-right"><b>' + formatNumber_N1(data[5]) + '</b></div>';
                        rez += '</div>';
                        return rez;
                    }
                },
            },
            axisPointer: {
                link: [
                    {
                        xAxisIndex: 'all'
                    }
                ],
                label: {
                    backgroundColor: '#777'
                }
            },
            visualMap: [{
                show: false,
                seriesIndex: 0,
                dimension: 6,
                pieces: [
                    {
                        value: 1,
                        color: upColor,
                    },
                    {
                        value: -1,
                        color: downColor,
                    }
                ]
            }, {
                show: false,
                seriesIndex: 1,
                dimension: 6,
                pieces: [
                    {
                        value: 1,
                        color: upColor,
                    },
                    {
                        value: -1,
                        color: downColor,
                    }
                ]
            }],
            xAxis: [
                {
                    type: 'category',
                    demension: 0,
                    scale: true,
                    axisLabel: { color: textColor, },
                    axisLine: { onZero: false, lineStyle: { color: textColor }, },
                    axisTick: { lineStyle: { color: textColor } },
                    splitLine: { show: false },
                    min: 'dataMin',
                    max: 'dataMax',
                    axisPointer: {
                        z: 100
                    },
                },
                {
                    type: 'category',
                    demension: 0,
                    gridIndex: 1,
                    scale: true,
                    axisLabel: { show: false },
                    axisLine: { onZero: false, lineStyle: { color: textColor }, },
                    axisTick: { show: false },
                    splitLine: { show: false },
                    min: 'dataMin',
                    max: 'dataMax',
                }
            ],
            yAxis: [
                {
                    scale: true,
                    splitLine: {
                        lineStyle: {
                            color: "#6E7079",
                            type: 'dashed',
                        }
                    },
                    axisLabel: { color: textColor, },
                    axisLine: { onZero: false, lineStyle: { color: textColor } },
                },
                {
                    scale: true,
                    gridIndex: 1,
                    splitNumber: 2,
                    axisLabel: { show: false },
                    axisLine: { show: false },
                    axisTick: { show: false },
                    splitLine: { show: false },
                    min: 0,
                }
            ],
            series: [
                {
                    name: 'Price',
                    type: 'candlestick',
                    itemStyle: {
                        color: upColor,
                        color0: downColor,
                        borderColor: upColor,
                        borderColor0: downColor,
                        borderWidth: 1,
                    },
                    encode: {
                        x: 0,
                        y: [1, 2, 3, 4],
                    },
                    barMaxWidth: 30,
                    barMinWidth: 1,
                    //
                    // markLine: {
                    //     silent: true,
                    //     symbol: 'none',
                    //     animation: false,
                    //     data: [
                    //         // case B
                    //         markLinePoints.length > 0 ?
                    //             [
                    //                 {
                    //                     coord: [markLinePoints[0].time, markLinePoints[0].value]
                    //                 },
                    //                 {
                    //                     coord: [markLinePoints[markLinePoints.length - 1].time, markLinePoints[markLinePoints.length - 1].value]
                    //                 }
                    //             ] : []
                    //     ]
                    // }
                },
                {
                    name: 'Volume',
                    type: 'bar',
                    xAxisIndex: 1,
                    yAxisIndex: 1,
                    encode: {
                        x: 0,
                        y: 5,
                    },
                    barMaxWidth: 30,
                }
            ],
            dataZoom: [
                {
                    type: 'inside',
                    xAxisIndex: [0, 1],
                    start: refZoomSetting.current ? refZoomSetting.current.start : undefined,
                    end: refZoomSetting.current ? refZoomSetting.current.end : undefined,
                    startValue: !refZoomSetting.current ? (_dataSet && _dataSet.length > 0 ? _dataSet.length - 121 : 0) : undefined,
                    endValue: !refZoomSetting.current ? (_dataSet && _dataSet.length > 0 ? _dataSet.length - 1 : 0) : undefined,
                },
                {
                    show: true,
                    xAxisIndex: [0, 1],
                    type: 'slider',
                    bottom: 10,
                    start: refZoomSetting.current ? refZoomSetting.current.start : undefined,
                    end: refZoomSetting.current ? refZoomSetting.current.end : undefined,
                    startValue: !refZoomSetting.current ? (_dataSet && _dataSet.length > 0 ? _dataSet.length - 121 : 0) : undefined,
                    endValue: !refZoomSetting.current ? (_dataSet && _dataSet.length > 0 ? _dataSet.length - 1 : 0) : undefined,
                }
            ],
            grid: [{
                left: _dataSet && _dataSet.length > 0 ? '70px' : '20px',
                right: '20px',
                bottom: '30%',
            },
            {
                left: _dataSet && _dataSet.length > 0 ? '70px' : '20px',
                right: '20px',
                top: '75%',
                bottom: '50px',
            }],
        }
    }

    const _handleWindowResize = () => {
        if (refChart && refChart.current) {
            refChart.current.getEchartsInstance().resize();
        }
    }

    useEffect(() => {
        if (refChart && refChart.current) {
            refChart.current.getEchartsInstance().setOption(_getChartOptions());
        }
    });

    useEffect(() => {
        window.addEventListener('resize', _handleWindowResize);
        return () => {
            window.removeEventListener('resize', _handleWindowResize);
        };
    }, []);

    const _handleDataZoom = (params) => {
        refZoomSetting.current = { start: params.start, end: params.end };
    }

    //
    return (
        <ReactECharts style={{ width: '100%', height: '100%', ...props.style }}
            ref={refChart}
            lazyUpdate={true}
            option={_getChartOptions()}
            onEvents={{
                'datazoom': _handleDataZoom,
            }}
        />
    )
}, (prevProps, nextProps) => {
    if (prevProps.indexCode === nextProps.indexCode) {
        return true;
    }
    return false;
})
